import { Button, Typography } from "@suraasa/placebo-ui"
import { AssignmentAttachment } from "api/resources/assignments/types"
import Doc from "features/LearningItems/Resources/icons/doc.svg"
import Excel from "features/LearningItems/Resources/icons/excel.svg"
import File from "features/LearningItems/Resources/icons/file.svg"
import Image from "features/LearningItems/Resources/icons/image.svg"
import Pdf from "features/LearningItems/Resources/icons/pdf.svg"
import Ppt from "features/LearningItems/Resources/icons/powerpoint.svg"
import Text from "features/LearningItems/Resources/icons/txt.svg"
import Video from "features/LearningItems/Resources/icons/video.svg"
import { ArrowRight, Download } from "iconoir-react"
import { downloadFile, pluralize } from "utils/helpers"

import AttachmentStack from "./AttachmentStack"

export const getExtension = (name: string) =>
  name.split("?")[0].split(".").pop()?.toLowerCase() ?? ""

const docExts = ["doc", "docx"]
const pptExts = ["ppt", "pptx"]
const excelExts = ["xls", "xlsx", "csv"]
const textExts = ["txt", "rtf"]
const imageExts = ["png", "jpg", "jpeg", "gif", "svg", "webp", "avif"]
const videoExts = [
  "mp4",
  "mov",
  "avi",
  "mkv",
  "webm",
  "m4v",
  "wmv",
  "flv",
  "mpg",
  "mpeg",
  "3gp",
  "ogv",
]

const getFileIconSrc = (ext: string) => {
  switch (true) {
    case ext === "pdf":
      return Pdf
    case docExts.includes(ext):
      return Doc
    case pptExts.includes(ext):
      return Ppt
    case excelExts.includes(ext):
      return Excel
    case textExts.includes(ext):
      return Text
    case imageExts.includes(ext):
      return Image
    case videoExts.includes(ext):
      return Video
    default:
      return File
  }
}

const FileTypeIcon = ({
  name,
  className,
}: {
  name: string
  className?: string
}) => {
  const ext = getExtension(name)
  return (
    <img src={getFileIconSrc(ext)} alt={ext || "file"} className={className} />
  )
}

const AttachmentCard = ({
  attachments,
  onViewAll,
}: {
  attachments: AssignmentAttachment[]
  onViewAll?: () => void
}) => {
  if (attachments.length === 0) return null

  const isMultiple = attachments.length > 1
  const first = attachments[0]

  const handleDownload = () => downloadFile(first.file, first.name)

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-xl border border-highlight-200 bg-highlight-50 p-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-row items-center gap-1.5">
        <div className="flex size-[60px] shrink-0 items-center justify-center rounded-lg bg-surface">
          {isMultiple ? (
            <AttachmentStack count={attachments.length} />
          ) : (
            <FileTypeIcon name={first.name} className="size-4" />
          )}
        </div>
        <div className="flex min-w-0 flex-col items-start gap-1">
          <Typography
            variant="subtitle2"
            className="max-w-full break-words text-onSurface-800"
          >
            {isMultiple
              ? `${pluralize(
                  "resource",
                  attachments.length
                )} for this assignment`
              : first.name}
          </Typography>
          <Typography className="text-onSurface-800">
            {isMultiple
              ? "These resources are specific to this assignment. Use only these versions for your submission."
              : "This resource is specific to this assignment. Use only this version for your submission."}
          </Typography>
        </div>
      </div>
      <Button
        onClick={isMultiple ? onViewAll : handleDownload}
        endAdornment={
          isMultiple ? (
            <ArrowRight className="!size-2 stroke-[1.5px] text-onSurface-900" />
          ) : (
            <Download className="!size-2 stroke-[1.5px] text-onSurface-900" />
          )
        }
        className="shrink-0 border border-onSurface-300 bg-surface px-1.5 py-[7px] text-strongSmallBody text-onSurface-700 max-sm:w-full"
      >
        {isMultiple ? "View all" : "Download"}
      </Button>
    </div>
  )
}

export default AttachmentCard
